import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { notify } from '../../notifications';
import { apiBlobError, apiError, downloadBlob } from '../Documents/format';
import { useLoad } from '../OnlineJournal/hooks/useLoad';
import EnrichmentStatus from './components/EnrichmentStatus';
import NomenclatureToolbar from './components/NomenclatureToolbar';
import ProductDialog from './components/ProductDialog';
import ProductsTable from './components/ProductsTable';
import { NomenclatureMeta, SortKey, isCancelled, isForbidden, nomenclatureApi } from './nomenclatureApi';
import { DEFAULT_SORT, EMPTY_FILTERS, Filters, Sort, countFilters, enrichmentBusy, filterOptions, toQuery } from './nomenclatureCalc';

interface MetaError { text: string; forbidden: boolean }

/** Пока идёт загрузка из 1С или фоновая сверка, состояние опрашивается тихо (/meta в журнал не пишется) */
const META_POLL_MS = 10000;
const ENRICH_POLL_MS = 30000;

/**
 * «Справочник» (справочная): уникальная номенклатура 1С без цен, дополненная открытыми источниками -
 * Национальным каталогом товаров (с ОФД) и госреестром лекарственных средств НЦЭЛС.
 * Сверка с источниками идёт в фоне на сервисе номенклатуры, экран читает только кеш.
 */
const NomenclatureModule: React.FC = () => {
  const [meta, setMeta] = useState<NomenclatureMeta | null>(null);
  const [metaError, setMetaError] = useState<MetaError | null>(null);
  const [term, setTerm] = useState('');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState<number | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [openZid, setOpenZid] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    document.title = 'Справочник | Zerde';
  }, []);

  /** quiet - фоновое обновление: прежние данные и экран ошибки не трогаются */
  const loadMeta = useCallback((signal?: AbortSignal, quiet = false) => {
    if (!quiet) setMetaError(null);
    return nomenclatureApi.meta(signal)
      .then(res => {
        setMeta(res);
        setLimit(l => l || res.limits.page);
      })
      .catch(err => {
        if (isCancelled(err) || quiet) return;
        setMetaError({ text: apiError(err, 'Не удалось загрузить справочник'), forbidden: isForbidden(err) });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadMeta(controller.signal);
    return () => controller.abort();
  }, [loadMeta]);

  const syncing = Boolean(meta && meta.data && meta.data.isLoading);
  const busy = syncing || enrichmentBusy(meta);
  useEffect(() => {
    if (!busy) return undefined;
    const controller = new AbortController();
    const timer = window.setInterval(() => { loadMeta(controller.signal, true); }, syncing ? META_POLL_MS : ENRICH_POLL_MS);
    return () => {
      window.clearInterval(timer);
      controller.abort();
    };
  }, [busy, syncing, loadMeta]);

  // загрузка из 1С закончилась - открытая страница списка перечитывается по новым данным (тихо)
  const wasSyncing = useRef(false);
  useEffect(() => {
    if (wasSyncing.current && !syncing) setReloadToken(t => t + 1);
    wasSyncing.current = syncing;
  }, [syncing]);

  const query = useMemo(() => toQuery(term, filters, sort), [term, filters, sort]);
  // в журнал пишется поиск или отбор; листание, сортировка и смена размера страницы - тот же просмотр
  const filterKey = JSON.stringify({ term, filters });
  const auditedRef = useRef<string | null>(null);
  const pageSize = limit || (meta ? meta.limits.page : 0);
  const listKey = JSON.stringify({ q: query, page, limit: pageSize });
  const list = useLoad(
    meta && pageSize
      ? (silent: boolean, signal: AbortSignal) => nomenclatureApi.list(query, page * pageSize, pageSize, silent || auditedRef.current === filterKey, signal)
        .then(res => {
          auditedRef.current = filterKey;
          return res;
        })
      : null,
    listKey, reloadToken, 'Не удалось загрузить справочник'
  );
  // данные прежнего запроса остаются на экране, только пока грузится новый
  const data = list.stale && !list.loading ? null : list.data;

  const search = useCallback((value: string) => {
    setTerm(value);
    setPage(0);
  }, []);
  const patchFilters = useCallback((patch: Partial<Filters>) => {
    setFilters(f => ({ ...f, ...patch }));
    setPage(0);
  }, []);
  const resetFilters = useCallback(() => {
    setTerm('');
    setFilters(EMPTY_FILTERS);
    setPage(0);
  }, []);
  const sortBy = useCallback((key: SortKey) => {
    setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
    setPage(0);
  }, []);
  const changeLimit = useCallback((value: number) => {
    setLimit(value);
    setPage(0);
  }, []);
  const closeProduct = useCallback(() => setOpenZid(null), []);

  const exportExcel = useCallback(async () => {
    setExporting(true);
    try {
      const file = await nomenclatureApi.exportFile(query);
      downloadBlob(file.filename, file.blob);
    } catch (err) {
      if (!isCancelled(err)) notify.error(await apiBlobError(err, 'Не удалось выгрузить справочник'));
    } finally {
      setExporting(false);
    }
  }, [query]);

  const options = useMemo(() => (meta ? filterOptions(meta) : null), [meta]);

  if (metaError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity={metaError.forbidden ? 'warning' : 'error'}
          action={metaError.forbidden ? undefined : <Button color="inherit" size="small" onClick={() => { loadMeta(); }}>Повторить</Button>}
        >
          {metaError.forbidden ? 'У вас нет доступа к справочнику. Обратитесь к администратору.' : metaError.text}
        </Alert>
      </Box>
    );
  }

  if (!meta || !options) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} /></Box>;
  }

  const hasFilters = Boolean(term) || countFilters(filters) > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
      <EnrichmentStatus meta={meta} />

      <NomenclatureToolbar
        meta={meta} options={options} term={term} filters={filters} loading={list.loading}
        total={data && !list.stale ? data.total : null} exporting={exporting}
        onSearch={search} onFilter={patchFilters} onReset={resetFilters} onExport={exportExcel}
      />

      {list.error && !list.loading && (
        <Alert
          severity={list.error.forbidden ? 'warning' : 'error'}
          action={list.error.forbidden ? undefined : <Button color="inherit" size="small" onClick={list.retry}>Повторить</Button>}
        >
          {list.error.text}
        </Alert>
      )}

      <ProductsTable
        data={data} page={page} limit={pageSize} sort={sort} loading={list.loading} hasFilters={hasFilters}
        onPage={setPage} onLimit={changeLimit} onSort={sortBy} onOpen={setOpenZid} onResetFilters={resetFilters}
      />

      {/* карточка размонтируется при закрытии: повторное открытие - новый просмотр в журнале */}
      {openZid && <ProductDialog zid={openZid} onOpen={setOpenZid} onClose={closeProduct} />}
    </Box>
  );
};

export default NomenclatureModule;
